<template>
  <div class="special-triangle-viz">
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>核心：</strong>{{ activeFormula }}$</div>
    </div>

    <div class="main-layout vertical">
      <div class="canvas-box simulation">
        <svg width="100%" height="300" viewBox="0 0 500 300">
          <defs>
             <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
             </filter>
             <linearGradient id="tri-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style="stop-color:#00d2ff;stop-opacity:0.2" />
                <stop offset="100%" style="stop-color:#00d2ff;stop-opacity:0.05" />
             </linearGradient>
          </defs>

          <!-- The Triangle with transition-like reactive points -->
          <g transform="translate(250, 50)">
             <!-- Shadow shape -->
             <path :d="trianglePath" fill="url(#tri-grad)" stroke="#00d2ff" stroke-width="3" filter="url(#glow)" style="transition: d 0.5s cubic-bezier(0.4, 0, 0.2, 1)" />
             
             <!-- Equal Side Markers (Isosceles) -->
             <g v-if="mode === 'isosceles' || mode === 'iso_right'">
                <line :x1="markers.left.x1" :y1="markers.left.y1" :x2="markers.left.x2" :y2="markers.left.y2" stroke="#ffd700" stroke-width="2" />
                <line :x1="markers.right.x1" :y1="markers.right.y1" :x2="markers.right.x2" :y2="markers.right.y2" stroke="#ffd700" stroke-width="2" />
             </g>

             <!-- Right Angle Marker -->
             <g v-if="mode === 'right' || mode === 'iso_right'" :transform="`translate(${points[1].x}, ${points[1].y})`">
                <rect x="0" y="-15" width="15" height="15" fill="none" stroke="#ffd700" stroke-width="2" />
             </g>

             <!-- Three Lines in One (Isosceles) -->
             <g v-if="showProperties && (mode === 'isosceles' || mode === 'iso_right')">
                <line x1="0" y1="0" x2="0" :y2="triHeight" stroke="#ff4757" stroke-width="2" stroke-dasharray="4" />
                <circle cx="0" :cy="triHeight" r="3" fill="#ff4757" />
                <text x="5" y="100" fill="#ff4757" font-size="10" font-weight="bold">三线合一</text>
             </g>

             <!-- Point interaction circles -->
             <circle v-for="(p, i) in points" :key="i" :cx="p.x" :cy="p.y" r="5" fill="#fff" />
          </g>
        </svg>

        <div class="hud-top">
          <div class="stat">
             <label>当前模式</label>
             <div class="value">{{ modeLabels[mode] }}</div>
          </div>
          <div class="stat" v-if="mode === 'right'">
             <label>勾股校验</label>
             <div class="value" style="color: #2ed573">{{ a }}^2 + {{ b }}^2 = {{ c }}^2</div>
          </div>
        </div>
      </div>

      <div class="bottom-controls">
        <div class="control-card glass">
           <div class="row items-center justify-between">
              <div class="type-selector">
                 <el-radio-group v-model="mode" size="mini">
                   <el-radio-button label="isosceles">等腰性质</el-radio-button>
                   <el-radio-button label="right">直角性质</el-radio-button>
                   <el-radio-button label="iso_right">等腰直角</el-radio-button>
                 </el-radio-group>
              </div>
              <div class="toggle-box">
                 <el-switch v-model="showProperties" active-text="显示关键性质" size="mini"></el-switch>
              </div>
           </div>
           
           <div class="row sliders mt-4">
              <div class="flex-1">
                 <label>底边/直角边 B (控制跨度)</label>
                 <el-slider v-model="triWidth" :min="100" :max="300" size="mini"></el-slider>
              </div>
              <div class="flex-1">
                 <label>高度/直角边 A (控制深浅)</label>
                 <el-slider v-model="triHeight" :min="80" :max="220" size="mini"></el-slider>
              </div>
           </div>
        </div>

        <div class="discovery-note" v-latex>
          <p><strong>定理探索：</strong>
          <span v-if="mode === 'isosceles'">等腰三角形中，顶角的平分线、底边上的中线和底边上的高线“三线合一”。</span>
          <span v-else-if="mode === 'right'">直角三角形满足勾股定理。斜边上的中线长度等于斜边的一半。</span>
          <span v-else>等腰直角三角形是极特殊的几何体，其内角为 45°, 45°, 90°，底边与腰的比值为 √2。</span>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SpecialTriangleViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mode: 'isosceles',
      triWidth: 200,
      triHeight: 180,
      showProperties: true,
      modeLabels: {
        isosceles: '等腰三角形',
        right: '直角三角形',
        iso_right: '等腰直角'
      }
    }
  },
  computed: {
    points() {
      const w = this.triWidth / 2;
      const h = this.triHeight;
      if (this.mode === 'isosceles') {
        return [{ x: 0, y: 0 }, { x: -w, y: h }, { x: w, y: h }];
      } else if (this.mode === 'right') {
        return [{ x: -w, y: 0 }, { x: -w, y: h }, { x: w, y: h }];
      } else { // iso_right
        return [{ x: -h, y: h }, { x: -h, y: 0 }, { x: 0, y: h }];
      }
    },
    trianglePath() {
      const p = this.points;
      return `M ${p[0].x} ${p[0].y} L ${p[1].x} ${p[1].y} L ${p[2].x} ${p[2].y} Z`;
    },
    markers() {
      if (this.mode === 'isosceles' || this.mode === 'iso_right') {
        const p = this.points;
        return {
          left: { x1: (p[0].x+p[1].x)/2 - 5, y1: (p[0].y+p[1].y)/2 - 5, x2: (p[0].x+p[1].x)/2 + 5, y2: (p[0].y+p[1].y)/2 + 5 },
          right: { x1: (p[0].x+p[2].x)/2 - 5, y1: (p[0].y+p[2].y)/2 - 5, x2: (p[0].x+p[2].x)/2 + 5, y2: (p[0].y+p[2].y)/2 + 5 }
        }
      }
      return { left: {}, right: {} };
    },
    activeFormula() {
      if (this.mode === 'isosceles') return 'AB = AC ⇔ ∠B = ∠C';
      if (this.mode === 'right') return 'a² + b² = c² | r = c/2';
      return '1 : 1 : √2 | ∠A = ∠B = 45°';
    },
    // For HUD display
    a() { return Math.round(this.triHeight/20); },
    b() { return Math.round(this.triWidth/20); },
    c() { return Math.round(Math.hypot(this.a, this.b)); }
  },
  watch: {
    mode(val) {
      if (val === 'iso_right') {
        this.triWidth = this.triHeight * 2; // Keep proportional
      }
    }
  }
}
</script>

<style scoped lang="scss">
.special-triangle-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.canvas-box { background: #010205; border-radius: 16px; position: relative; border: 1px solid rgba(255,255,255,0.05); }
.hud-top { 
  position: absolute; width: 100%; padding: 20px; display: flex; justify-content: space-between;
  .stat { background: rgba(255,255,255,0.03); backdrop-filter: blur(10px); padding: 8px 15px; border-radius: 8px; border-left: 3px solid #00d2ff; }
}
.bottom-controls { flex: 0.8; display: flex; flex-direction: column; gap: 12px; padding: 0 20px 20px; }
.control-card { background: rgba(255,255,255,0.02); border-radius: 16px; padding: 20px; border: 1px solid rgba(255,255,255,0.05); }
.discovery-note { background: rgba(0, 210, 255, 0.05); padding: 15px 20px; border-radius: 12px; border: 1px solid rgba(0, 210, 255, 0.1); 
  p { margin: 0; font-size: 0.85rem; color: #aaa; line-height: 1.6; strong { color: #fff; } }
}
.row { display: flex; gap: 20px; align-items: center; }
.sliders { flex-direction: row; }
label { font-size: 0.75rem; color: #888; margin-bottom: 8px; display: block; }
.flex-1 { flex: 1; }
</style>
